import {
  Fragment,
  memo,
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import {
  useWorkToastStore,
  type WorkToastItem,
  type WorkToastQuestionOption,
} from "@/stores/work-toast-store";
import type { ApprovalDecision } from "@/lib/decide-approval";
import {
  workToastPrimaryOpenTarget,
} from "@/lib/work-toast-apps";
import { useT } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import { ToastCard } from "./toast-card";
import { useToastCardResize } from "./use-toast-card-resize";
import { cn } from "@/lib/cn";
import { MORPH_MS, REVEAL_EASE } from "@/lib/reveal-motion";
import { isComputerWorkToastPinned, isDrivingComputer } from "@/lib/work-toast-computer-pin";
import { ToastPill, type ToastPillAction } from "./toast-pill";
import { ToastReplyBox } from "./toast-reply-box";
import { workToastDot } from "./toast-status";
import { useToastReplyDictation } from "./use-toast-reply-dictation";
import { useWorkToastFollowUp } from "./use-work-toast-follow-up";
import { useTransientComposerDraft } from "@/hooks/use-transient-composer-draft";
import {
  answerWorkToastQuestion,
  decideWorkToastApproval,
  openWorkToastChat,
  openWorkToastAppTarget,
  requestWorkToastTask,
  sendWorkToastFollowUp,
} from "./work-toast-actions";
import {
  buildWorkToastHoverActions,
  buildWorkToastMenuActions,
  dispatchWorkToastClick,
  workToastTargetOfferable,
} from "./work-toast-click";
import { WorkToastActivityLog } from "./work-toast-activity-log";
import { WorkToastBrowserFillFields } from "./work-toast-browser-fill-fields";
import { WorkToastLogo } from "./work-toast-identity";
import { useWorkToastNames } from "./use-work-toast-names";
import { WorkToastApprovalBody } from "./work-toast-approval-body";
import { WorkToastQuestionBody } from "./work-toast-question-body";
import { WorkToastResultBody } from "./work-toast-result-body";
import { WorkToastScreenshotBody } from "./work-toast-screenshot-body";
import {
  workToastBehavior,
  workToastBlockedAction,
  workToastBodyParts,
  workToastBodyText,
  workToastCardSubtitle,
  workToastDetail,
  workToastDetailIcon,
  workToastTitle,
  type WorkToastBodyPart,
} from "./work-toast-view";

export { WorkToastLogo } from "./work-toast-identity";

/** One work item as a pill, or as its card while expanded (spec §4.1–§4.3).
 *  Wrapped in `memo`: `ToastStack` mounts one per visible item, and `item`
 *  is a stable reference from the store unless that specific item changed
 *  (see `work-toast-store.ts`'s shallow-merge `patchItem`), so unrelated
 *  item updates elsewhere in the stack don't re-render this one. */
export const WorkToastPill = memo(function WorkToastPill({
  item,
  expanded,
  displayId,

  embedded = false,
  onOpenChat,
  onShowFullPanel,
  onMenuOpenChange,
}: {
  item: WorkToastItem;
  expanded: boolean;
  displayId: number | null;

  embedded?: boolean;
  onOpenChat?: (item: WorkToastItem) => void;
  onShowFullPanel?: () => void;
  onMenuOpenChange?: (key: string, open: boolean) => void;
}) {
  const t = useT(toastsMessages);
  const { projectName, taskTitle, conversationTitle } = useWorkToastNames(item);
  const handleMenuOpenChange = useCallback(
    (open: boolean) => onMenuOpenChange?.(item.key, open),
    [item.key, onMenuOpenChange],
  );
  const logo = <WorkToastLogo item={item} />;
  const followUpAvailable = useWorkToastFollowUp(item);
  // Expanded is only a request: with nothing to show the item stays the
  // compact row, and grows into the card when content arrives. Live work
  // keeps an expanded card while waiting for its next activity.
  const parts = workToastBodyParts(item, { followUpAvailable, taskTitle });
  const driving = isDrivingComputer(item);
  const showCard = expanded && (parts.length > 0 || item.state === "working");
  // The card takes focus only when the user opened it and it is there at the
  // moment of expanding. One Denker revealed by itself, or one that appears
  // later because content arrived, must not pull focus from where they type.
  const expandedByUser = useWorkToastStore((state) => state.expandedByUser);
  const wasExpanded = useRef(false);
  const focusCard = useRef(false);
  focusCard.current = expanded !== wasExpanded.current
    ? showCard && expandedByUser
    : focusCard.current && showCard;
  wasExpanded.current = expanded;
  // The only two states whose card offers a reply box (spec §4.2/§6.4) —
  // used both to gate the hover reply actions' focus intent and to drop an
  // in-flight recording the moment the item state carries it elsewhere.
  const replyCapable =
    item.state === "question" || (item.state === "done" && followUpAvailable);
  const [draft, setDraft] = useTransientComposerDraft(JSON.stringify([
    "work-toast", item.projectId, item.conversationId, item.key,
    item.state === "question" ? item.question?.awaitingInputId : "follow-up",
    item.state === "question" ? item.question?.index : undefined,
  ]), embedded);
  const [busy, setBusy] = useState(false);
  // Tracks *why* the card is open, so the right initial focus can be
  // requested from it — never a second focus mechanism of our own (the
  // card owns focus-on-mount; see toast-card.tsx).
  const [openedVia, setOpenedVia] = useState<"reply" | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { ref: expandRef, height: expandHeight } = useToastCardResize(
    showCard,
    item.key,
  );
  // Ref-backed guards (not `busy`) so a second click within the same tick —
  // before the `busy` state commit is visible to a freshly bound handler —
  // can't double-submit.
  const sendingRef = useRef(false);
  const decidingRef = useRef(false);

  const send = useCallback(
    async (text: string, selectedOption = "other", imageIds: string[] = []) => {
      const trimmed = text.trim();
      const answerable = item.state === "question";
      const followUpable = item.state === "done" && followUpAvailable;
      if ((!trimmed && !imageIds.length) || sendingRef.current || (!answerable && !followUpable))
        return false;
      sendingRef.current = true;
      setBusy(true);
      try {
        const ok = answerable
          ? await answerWorkToastQuestion(item, trimmed, selectedOption, imageIds)
          : await sendWorkToastFollowUp(item, trimmed, imageIds);
        if (ok) setDraft("", draft);
        return ok;
      } finally {
        sendingRef.current = false;
        setBusy(false);
      }
    },
    [draft, followUpAvailable, item, setDraft],
  );

  const dictation = useToastReplyDictation({
    id: `work-toast:${item.key}`,
    value: draft,
    onChange: setDraft,
    onSubmit: (text) => void send(text),
  });
  const { cancelOwnedRecording } = dictation;

  // Collapsing, or the expanded item leaving a reply-capable state (e.g. a
  // question resolving to "working" mid-recording — spec §6.4), must drop an
  // owned recording. Otherwise its final transcript reaches `send` after the
  // state has already moved on, where it is now silently ignored instead of
  // being answered or sent as a stray follow-up.
  useEffect(() => {
    if (!expanded || !replyCapable) cancelOwnedRecording();
  }, [cancelOwnedRecording, expanded, replyCapable]);

  // Reset the "opened for a reply" intent whenever the pill isn't expanded,
  // so a later expand this component didn't itself trigger (e.g. from a
  // "+N need you" badge elsewhere) never inherits a stale "reply" focus
  // request from a previous open.
  useEffect(() => {
    if (!expanded) setOpenedVia(null);
  }, [expanded]);

  const store = useWorkToastStore;
  const openChat = () => {
    if (onOpenChat) onOpenChat(item);
    else void openWorkToastChat(item);
  };
  // React fires no mouse-leave when a hovered node unmounts, and a stuck
  // hoveredKey keeps a Done pill visible on every display.
  useEffect(
    () => () => {
      const state = store.getState();
      if (state.hoveredKey === item.key) state.setHovered(null);
    },
    [item.key, store],
  );
  const expand = useCallback(
    () => store.getState().expand(item.key, displayId),
    [displayId, item.key, store],
  );
  const collapse = useCallback(() => {
    const state = store.getState();
    if (state.expandedKey === item.key && state.expandedDisplayId === displayId)
      state.collapse();
  }, [displayId, item.key, store]);
  // A settled item with nothing to show must not stay "expanded": the row
  // would never fade and would keep the rail folded. A working row stays
  // expanded so it can still grow into the card.
  useEffect(() => {
    if (expanded && !showCard && item.state !== "working") collapse();
  }, [collapse, expanded, item.state, showCard]);
  const decide = useCallback(
    async (decision: ApprovalDecision) => {
      if (decidingRef.current) return;
      decidingRef.current = true;
      setBusy(true);
      try {
        await decideWorkToastApproval(item, decision);
      } finally {
        decidingRef.current = false;
        setBusy(false);
      }
    },
    [item],
  );
  const pickOption = useCallback(
    (option: WorkToastQuestionOption) => void send(option.label, option.id),
    [send],
  );

  const behavior = workToastBehavior(item, {
    followUpAvailable,
    micAvailable: Boolean(dictation.mic),
  });
  const hoverActions = buildWorkToastHoverActions(item, {
    micAction: dictation.mic,
    reply: item.state === "question" ? "answer" : "followUp",
    expand,
    setOpenedVia,
    busy,
    decide: (decision) => void decide(decision),
  });
  // A run driving the computer never offers the app it is working in: its
  // arrows and its row click open the chat, on every platform.
  const primaryOpenTarget = driving ? null : workToastPrimaryOpenTarget(item);
  // App links require a destination that can open independently.
  const offerableOpenTarget =
    primaryOpenTarget && workToastTargetOfferable(primaryOpenTarget) ? primaryOpenTarget : null;
  const openHoverAction: ToastPillAction | null = item.conversationId && item.state !== "done"
    ? {
        id: "open",
        label: t("openChat"),
        icon: "ArrowUpRight",
        onClick: openChat,
      }
    : null;
  const menuActions = buildWorkToastMenuActions(item, {
    openChat,
    primaryOpenTarget: offerableOpenTarget,
    dismiss: () => store.getState().dismiss(item.key),
  });
  // Drop the "open-in-app" entry when the primary target isn't offerable —
  // `behavior.menu` only knows the item has *some* open target, not whether
  // it's usable here, so it would otherwise render a dead menu item.
  const menuItems = behavior.menu
    .filter((id) => id !== "open-in-app" || offerableOpenTarget)
    .map((id) => menuActions[id]);
  const openChatAction: ToastPillAction = {
    id: "open-chat",
    label: t("openChat"),
    onClick: openChat,
  };

  if (showCard) {
    const replyBox = (
      <ToastReplyBox
        testId="work-toast-reply"
        value={draft}
        onChange={setDraft}
        onSend={(imageIds) => send(draft, "other", imageIds)}
        placeholder={
          item.state === "question"
            ? t("answerPlaceholder")
            : t("followUpPlaceholder")
        }
        sending={busy || !item.projectId}
        mic={dictation.mic}
        textareaRef={textareaRef}
      />
    );
    const chatAction = !embedded && item.conversationId ? openChatAction : undefined;
    const bodyText = workToastBodyText(item, taskTitle);
    const blockedAction = workToastBlockedAction(item);
    const renderPart = (part: WorkToastBodyPart): ReactNode => {
      switch (part) {
        case "question":
          return item.question && (
            <WorkToastQuestionBody
              question={item.question}
              disabled={busy || !item.projectId}
              onPickOption={pickOption}
              reply={replyBox}
            />
          );
        case "approval":
          return item.approval && (
            <WorkToastApprovalBody
              approval={item.approval}
              disabled={busy || !item.projectId}
              onDecide={(decision) => void decide(decision)}
            />
          );
        case "result":
          return (
            <WorkToastResultBody
              key={item.activity?.[item.activity.length - 1]?.id}
              text={bodyText}
              bounded
              compact={item.state === "working" && item.resultSuperseded}
              reply={item.state === "done" && followUpAvailable ? replyBox : undefined}
              action={parts[0] === "result" ? chatAction : undefined}
            />
          );
        case "error":
          return <WorkToastResultBody text={item.detail} bounded={false} action={chatAction} />;
        case "blocked":
          return (
            <WorkToastResultBody
              text={bodyText}
              bounded={false}
              action={
                blockedAction === "open-task"
                  ? { id: "open-task", label: t("openTask"), onClick: () => requestWorkToastTask(item) }
                  : blockedAction ? openChatAction : undefined
              }
              alwaysShowAction
            />
          );
        case "activity":
          return <WorkToastActivityLog entries={item.activity ?? []} action={chatAction} />;
        case "screenshot":
          return <WorkToastScreenshotBody screenshots={item.computerScreenshots ?? []} />;
        case "fields":
          return <WorkToastBrowserFillFields names={item.computer?.fieldNames ?? []} />;
      }
    };
    return (
      <div
        ref={expandRef}
        className={cn("denker-toast-resize", embedded && "w-full")}
        data-testid="work-toast-expand-wrap"
        style={{
          height: expandHeight ?? undefined,
          "--denker-toast-resize": `${MORPH_MS}ms`,
          "--denker-toast-ease": REVEAL_EASE,
        } as CSSProperties}
      >
        <ToastCard
          toastKey={item.key}
          focusOnMount={focusCard.current && !isComputerWorkToastPinned(item) && (item.state !== "working" || parts.length > 0)}
          state={item.state}
          logo={logo}
          title={workToastTitle(item, projectName, taskTitle, conversationTitle)}
          subtitle={workToastCardSubtitle(item)}
          subtitleIcon={workToastDetailIcon(item)}
          menuItems={driving ? [] : menuItems}
          onMenuOpenChange={handleMenuOpenChange}
          onStop={behavior.hover.includes("stop") ? hoverActions.stop.onClick : undefined}
          onCollapse={onShowFullPanel ?? collapse}
          collapseLabel={onShowFullPanel ? t("showFullPanel") : undefined}
          onOpenApp={
            embedded &&
            offerableOpenTarget &&
            (item.state === "working" || item.state === "done")
              ? () => openWorkToastAppTarget(offerableOpenTarget)
              : undefined
          }
          onOpenChat={embedded && item.conversationId ? openChat : undefined}
          initialFocusRef={openedVia === "reply" ? textareaRef : undefined}
          embedded={embedded}
        >
          {parts.map((part) => <Fragment key={part}>{renderPart(part)}</Fragment>)}
        </ToastCard>
      </div>
    );
  }

  // Not through the dispatcher for a driving run: its "open-chat" would first
  // reopen the app the agent last worked in.
  const onBodyClick =
    behavior.click === "none"
      ? undefined
      : driving && behavior.click === "open-chat"
      ? openChat
      : () =>
          dispatchWorkToastClick(behavior.click, item, {
            openChat,
            expand,
            replyCapable,
            setOpenedVia,
          });

  return (
    <ToastPill
      toastKey={item.key}
      state={item.state}
      logo={logo}
      title={workToastTitle(item, projectName, taskTitle, conversationTitle)}

      detail={workToastDetail(item, taskTitle)}
      detailIcon={workToastDetailIcon(item)}
      dot={workToastDot(item.state)}

      actions={[
        ...behavior.hover.map((id) => hoverActions[id]),
        ...(openHoverAction ? [openHoverAction] : []),
      ]}
      actionsPinned={
        item.source === "native_worker" &&
        item.state === "done" &&
        followUpAvailable
      }
      menuItems={menuItems}
      onMenuOpenChange={handleMenuOpenChange}
      onBodyClick={onBodyClick}
      onHoverChange={(hovered) => {
        const state = store.getState();
        if (hovered) state.setHovered(item.key);
        else if (state.hoveredKey === item.key) state.setHovered(null);
      }}
      embedded={embedded}
    />
  );
});
